import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import {
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSuffix } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BANK_LIST } from 'app/shared/constants/bank-list.constant';
import { DateTime } from 'luxon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, take, takeUntil } from 'rxjs';
import SignaturePad from 'signature_pad';
import {
    AddressAutocompleteComponent,
    AddressData,
    AddressValidationResult,
} from '../../../shared/components/address-autocomplete/address-autocomplete.component';
import { EmployeeCertificatesManagerComponent } from '../../admin/employee-management/employee-certificates-manager/employee-certificates-manager.component';
import { EmployeeCertificate } from '../../admin/employee-management/employee-management.types';
import { PartTimerManagementService } from '../../admin/part-timer-management/part-timer-management.service';
import { FoodHandlingServicesComponent } from '../contractor-service-agreement/food-handling-services/food-handling-services.component';
import { PromoterServicesComponent } from '../contractor-service-agreement/promoter-services/promoter-services.component';
import { TranslationService } from '../translation/translation.service';

declare const require: any;

interface DocTemplateData {
    position: string;
    clientName: string;
    nricFin: string;
    address: string;
    signature: string;
    currentDate: string;
}

interface AgeValidation {
    isValid: boolean;
    message: string;
    requiresDocument: boolean;
}

@Component({
    selector: 'app-part-timer',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatProgressSpinner,
        MatDatepickerInput,
        MatDatepicker,
        MatDatepickerToggle,
        MatSuffix,
        AddressAutocompleteComponent,
        EmployeeCertificatesManagerComponent,
    ],
    templateUrl: './part-timer.component.html',
    styleUrl: './part-timer.component.scss',
    providers: [DatePipe],
})
export class PartTimerComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('signaturePadCanvas') signaturePadCanvas!: ElementRef;
    @ViewChild('previewContainer', { static: false })
    previewContainer!: ElementRef;
    @ViewChild(EmployeeCertificatesManagerComponent)
    certificatesManager!: EmployeeCertificatesManagerComponent;
    @ViewChild(AddressAutocompleteComponent)
    addressAutocomplete?: AddressAutocompleteComponent;
    signaturePad!: SignaturePad;
    generatedDocument: Blob | null = null;

    registrationForm: FormGroup;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    currentFormIndex = 0;
    totalForm = 5;
    submitting = false;

    // Form checkboxes for additional details
    workingWithNexus: string = '';
    medication: string = '';

    public employeeCertificates: EmployeeCertificate[] = [];

    // Shared 90-bank list. See app/shared/constants/bank-list.constant.
    public bankList: string[] = BANK_LIST;

    public filteredBankList: string[] = [];
    public showBankDropdown: boolean = false;

    isProceedToBackend = false;

    // ---- IC lookup / resubmission state (step 1) ----
    icCheckStatus:
        | 'idle'
        | 'checking'
        | 'not_found'
        | 'active'
        | 'pending'
        | 'rejected'
        | 'blacklisted'
        | 'other_status'
        | 'possible_legacy_match'
        | 'error' = 'idle';
    // Translation key of the message to show under the IC field.
    icCheckMessageKey: string = '';
    // True once an ACTIVE record is found: submit updates the existing employee.
    isResubmission = false;
    resubmitEmployeeId: string | null = null;
    private lastCheckedIc: string = '';
    private icBlurDebounce: any = null;

    signaturePadOptions = {
        minWidth: 1,
        canvasWidth: 500,
        canvasHeight: 200,
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
    };

    public today: string = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();
    public requiresDocument: boolean = false;
    public temporaryAddress: string = '';
    public addressMode: 'single' | 'split' = 'single';
    public postalCodeValid: boolean = false;

    // Address components from Google Places
    public addressComponents: {
        formatted_address: string | null;
        location_lat: string | null;
        location_lng: string | null;
        street_number: string | null;
        street: string | null;
        city: string | null;
        state: string | null;
        country: string | null;
        country_code: string | null;
        place_id: string | null;
    } | null = null;

    constructor(
        private translationService: TranslationService,
        private _formBuilder: FormBuilder,
        private _messageService: NzMessageService,
        private _changeDetectorRef: ChangeDetectorRef,
        private http: HttpClient,
        private dialog: MatDialog,
        private datePipe: DatePipe,
        private dateAdapter: DateAdapter<any>,
        private _partTimerManagementService: PartTimerManagementService
    ) {
        this.dateAdapter.setLocale('en-GB');
    }

    ngOnInit() {
        this.initForm();

        // TEMP DIAGNOSTIC (remove after prefill issue resolved): expose the
        // component so the blocking invalid controls can be listed from the
        // browser console via __ptInvalid()
        (window as any).__ptInvalid = () =>
            Object.entries(this.registrationForm.controls)
                .filter(([, c]: [string, any]) => c.invalid)
                .map(([k, c]: [string, any]) => ({
                    field: k,
                    errors: c.errors,
                    value: c.value,
                }));

        this.filteredBankList = [...this.bankList];

        // Initialize temporaryAddress from the form if it has a value
        // (e.g., when coming back to this form in multi-step process)
        if (this.registrationForm.get('residential_address').value) {
            this.temporaryAddress = this.registrationForm.get(
                'residential_address'
            ).value;
        }
    }

    ngAfterViewInit() {
        this.signaturePad = new SignaturePad(
            this.signaturePadCanvas.nativeElement,
            {
                backgroundColor: 'rgb(255, 255, 255)',
                penColor: 'rgb(0, 0, 0)',
            }
        );
    }

    get workingExps() {
        return this.registrationForm.get('working_exps') as FormArray;
    }

    formatDate(date: any): string {
        if (!date) return '';

        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return '';

            const day = dateObj.getDate().toString().padStart(2, '0');
            const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
            const year = dateObj.getFullYear();

            return `${day}/${month}/${year}`;
        } catch (e) {
            console.error('Error formatting date:', e);
            return '';
        }
    }

    private initForm(): void {
        this.registrationForm = this._formBuilder.group({
            // Form 2 controls
            client_name: ['', Validators.required],
            contact_email: ['', [Validators.required, Validators.email]],
            // NOTE: the pattern used to sit in the ASYNC validator slot (3rd
            // element), which made Angular throw NG01101 on every validation
            // and abort programmatic patchValue mid-way (the prefill bug).
            // Moved into the sync array, and widened to accept Singapore or
            // Malaysia numbers, matching the kin_contact rule (the old
            // SG-only pattern never actually validated due to the bug).
            contact_number: [
                '',
                [
                    Validators.required,
                    Validators.pattern(
                        '^([89][0-9]{7}|(\\+?60)?01[0-9]{8,9})$'
                    ),
                ],
            ],
            // nric: ['', [Validators.required, Validators.pattern('^[A-Z][0-9]{7}[A-Z]$')]],
            date_of_birth: [null, [Validators.required, this.ageValidator()]],
            gender: ['', Validators.required],
            unit_number: [''], // New unit number field (optional)
            residential_address: ['', Validators.required],
            postal_code: ['', Validators.required],
            nationality: ['', Validators.required],
            pr_start_date: [null],
            id_type: ['', Validators.required],
            id_number: ['', [Validators.required, this.idNumberValidator()]],
            passport_expiry: [null],
            place_of_birth: ['', Validators.required],
            age: [{ value: '', disabled: true }, [this.ageMinValidator()]],
            race: ['', Validators.required],
            religion: ['', Validators.required],
            marital_status: ['', Validators.required],
            // Form 3 controls
            kin_name: ['', Validators.required],
            kin_contact: [
                '',
                [
                    Validators.required,
                    Validators.pattern(
                        '^([89][0-9]{7}|(\\+?60)?01[0-9]{8,9})$'
                    ),
                ],
            ],
            bank_name: ['', Validators.required],
            bank_acc: ['', Validators.required],
            // Form 1 controls
            where_know_us: ['', Validators.required],
            type_of_employment: ['parttime', Validators.required],
            position_applied: ['', Validators.required],
            earliest_availability: [null, Validators.required],
            fully_vaccinated: [0, Validators.required],
            // Form 4
            highest_education: [''],
            school: [''],
            country: ['Singapore'],
            date_start: [null],
            date_grad: [null],
            high_standard: [''],
            relatives_in_company: [0],
            relatives_info: [''],
            //other
            declaration: [false, Validators.requiredTrue],
            // QnA form group
            qna: this._formBuilder.group({
                where_know_us: [''],
                fully_vaccinated: [0],
                relatives_in_company: [0],
                relatives_info: [''],
                any_illness: [0],
                physical: [0],
                mental_illness: [0],
                medication: [0],
                medication_details: [''],
                found_guilty: [0],
                any_investigation: [0],
                objection_undergo: [0],
                alcohol_drug_addiction: [0],
                undischarged_bankrupt: [0],
                dismissed_or_suspended: [0],
                business_undertaking: [0],
            }),
            // Working experience array
            working_exps: this._formBuilder.array([]),
        });

        // Add conditional validator for relatives_info
        this.registrationForm
            .get('relatives_in_company')
            ?.valueChanges.subscribe((value) => {
                const relativesInfo =
                    this.registrationForm.get('relatives_info');
                if (value === 1) {
                    relativesInfo?.setValidators(Validators.required);
                } else {
                    relativesInfo?.clearValidators();
                }
                relativesInfo?.updateValueAndValidity();
            });

        this.registrationForm
            .get('date_of_birth')
            ?.valueChanges.subscribe((value) => {
                if (value) {
                    const age = this.calculateAge(value);
                    console.log('Current age:', age); // Debug log
                    if (age === 15) {
                        this.requiresDocument = true;
                    } else {
                        this.requiresDocument = false;
                    }
                    this.registrationForm.get('age').setValue(age);
                }
            });

        // Add conditional validator for medication details
        this.registrationForm
            .get('qna.medication')
            ?.valueChanges.subscribe((value) => {
                const medicationDetails = this.registrationForm.get(
                    'qna.medication_details'
                );
                if (value === 1) {
                    medicationDetails?.setValidators(Validators.required);
                } else {
                    medicationDetails?.clearValidators();
                }
                medicationDetails?.updateValueAndValidity();
            });

        this.registrationForm
            .get('id_type')
            ?.valueChanges.subscribe((value) => {
                const passportExpiryControl =
                    this.registrationForm.get('passport_expiry');
                if (value === 'Passport') {
                    passportExpiryControl?.setValidators([Validators.required]);
                } else {
                    passportExpiryControl?.clearValidators();
                }
                passportExpiryControl?.updateValueAndValidity();
            });

        // Subscribe to nationality changes
        this.registrationForm
            .get('nationality')
            ?.valueChanges.subscribe((value) => {
                const prStartDateControl =
                    this.registrationForm.get('pr_start_date');

                if (value === 'Singapore PR') {
                    prStartDateControl?.setValidators([Validators.required]);
                } else {
                    prStartDateControl?.clearValidators();
                    prStartDateControl?.setValue('');
                }
                prStartDateControl?.updateValueAndValidity();
            });

        this.registrationForm
            .get('earliest_availability')
            .valueChanges.subscribe((val) => {
                console.log('Date value changed:', val);
            });

        // Listen for unit_number changes to update address components
        this.registrationForm
            .get('unit_number')
            ?.valueChanges.subscribe((unitNumber) => {
                if (this.addressComponents) {
                    this.addressComponents.street_number =
                        unitNumber || this.addressComponents.street_number;
                }
            });

        this.addWorkingExperience();
    }

    onNationalityChange(event: any) {
        const value = event.target.value;
        if (value !== 'Singapore PR') {
            this.registrationForm.patchValue({
                pr_start_date: null,
            });
        }
    }

    isCertRequired(): boolean {
        const position = this.registrationForm.get('position_applied')?.value;
        const exemptPositions = [
            'Warehouse Assistant',
            'Retort Packing',
            'MK Admin',
            'Project Logistics Drivers',
        ];
        return !exemptPositions.includes(position);
    }

    // Add custom validator for ID number based on type
    private idNumberValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const idType = this.registrationForm?.get('id_type')?.value;
            const value = control.value?.toUpperCase();

            if (!value) return null;

            // Validate NRIC
            if (idType === 'NRIC') {
                const nricPattern = /^[ST]\d{7}[A-Z]$/;
                if (!nricPattern.test(value)) {
                    return { pattern: true };
                }

                const weights = [2, 7, 6, 5, 4, 3, 2];
                const firstChar = value.charAt(0);
                const digits = value.slice(1, 8);

                let total = 0;
                for (let i = 0; i < 7; i++) {
                    total += parseInt(digits[i]) * weights[i];
                }

                if (firstChar === 'T') {
                    total += 4;
                }

                const checksumChars = [
                    'J',
                    'Z',
                    'I',
                    'H',
                    'G',
                    'F',
                    'E',
                    'D',
                    'C',
                    'B',
                    'A',
                ];
                const expectedChecksum = checksumChars[total % 11];

                return value.charAt(8) === expectedChecksum
                    ? null
                    : { checksum: true };
            }

            // Validate FIN
            if (idType === 'FIN') {
                const finPattern = /^[FG]\d{7}[A-Z]$/;
                if (!finPattern.test(value)) {
                    return { pattern: true };
                }

                const weights = [2, 7, 6, 5, 4, 3, 2];
                const firstChar = value.charAt(0);
                const digits = value.slice(1, 8);

                let total = 0;
                for (let i = 0; i < 7; i++) {
                    total += parseInt(digits[i]) * weights[i];
                }

                if (firstChar === 'G') {
                    total += 4;
                }

                const checksumChars = [
                    'X',
                    'W',
                    'U',
                    'T',
                    'R',
                    'Q',
                    'P',
                    'N',
                    'M',
                    'L',
                    'K',
                ];
                const expectedChecksum = checksumChars[total % 11];

                return value.charAt(8) === expectedChecksum
                    ? null
                    : { checksum: true };
            }

            return null;
        };
    }

    currentLanguage = 'en';

    // Switch language
    switchLanguage(lang: string) {
        this.translationService.switchLanguage(lang);
        this.currentLanguage = lang;
    }

    // Get localized text
    getLocalizedText(key: string): string {
        return this.translationService.getLocalizedText(key);
    }

    getProgressWidth(): string {
        return (this.currentFormIndex / this.totalForm) * 100 + '%';
    }

    getFormClass(index: number): string {
        if (index === this.currentFormIndex) {
            return 'form form-enter-active';
        } else if (index < this.currentFormIndex) {
            return 'form form-exit-active';
        } else {
            return 'form form-enter';
        }
    }

    showRelativesInfo(): boolean {
        return this.registrationForm.get('relatives_in_company')?.value === 1;
    }

    public startDateFilter = (date: Date | null): boolean => {
        if (!date) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date >= today;
    };

    nextForm(): void {
        // Block progression on step 1 when the IC lookup returned a blocking status.
        if (this.currentFormIndex === 0 && this.icBlocked) {
            this._messageService.error(
                this.getLocalizedText(this.icCheckMessageKey)
            );
            return;
        }

        // Get the current form controls based on the form index
        const currentControls = this.getCurrentFormControls();

        // Mark all current fields as touched
        currentControls.forEach((controlName) => {
            const control = this.registrationForm.get(controlName);
            control?.markAsTouched();
        });

        // Check if current form fields are valid
        const isCurrentFormValid = currentControls.every(
            (controlName) => !this.registrationForm.get(controlName)?.invalid
        );

        // Step 1 is now the personal-details/address step: require a valid postal code
        if (this.currentFormIndex === 0) {
            // Check if address contains valid postal code
            if (!this.postalCodeValid) {
                this._messageService.error(
                    'Address must include a valid postal code'
                );
                return;
            }
        }

        if (this.currentFormIndex === 2) {
            // Form 3 - certificate validation
            if (
                this.isCertRequired() &&
                this.employeeCertificates.length === 0
            ) {
                this._messageService.error(
                    'Please upload at least one certificate'
                );
                return;
            }

            // Validate certificate completeness if any certificates have been added
            // Only enforce for positions that require certificates
            if (
                this.employeeCertificates.length > 0 &&
                this.isCertRequired() &&
                !this.validateCertificateCompleteness()
            ) {
                this.markInvalidCertificateFieldsAsTouched();
                this._messageService.error(
                    'Please complete all certificate fields or remove incomplete certificates'
                );
                return;
            }
        }

        if (isCurrentFormValid) {
            if (this.currentFormIndex === 0) {
                // Step 1 may only advance once the IC lookup has resolved for
                // the CURRENT value - never on idle/checking/error states.
                this.advanceFromIcStep();
                return;
            }
            if (this.currentFormIndex < this.totalForm) {
                this.currentFormIndex++;
            }
        } else {
            this._messageService.error(
                'Please fill in all required fields correctly.'
            );
        }
    }

    /**
     * Gate for leaving step 1: advance only when the lookup is RESOLVED for the
     * current normalized IC value (not_found or active). Idle or stale states
     * trigger the lookup now and advance in its success callback; 'checking'
     * waits; blocked states (incl. transport error) never advance.
     */
    private advanceFromIcStep(): void {
        if (this.icCheckStatus === 'checking') {
            return;
        }
        if (this.icBlocked) {
            this._messageService.error(
                this.getLocalizedText(
                    this.icCheckMessageKey || 'ic_check.error'
                )
            );
            return;
        }
        if (this.isIcResolvedForCurrentValue()) {
            this.currentFormIndex++;
            return;
        }
        // Idle or the IC changed since the last check: verify now and advance
        // only on a clean (not_found / active) result.
        this.checkIc(() => {
            if (this.isIcResolvedForCurrentValue()) {
                this.currentFormIndex++;
            }
        });
    }

    previousForm(): void {
        if (this.currentFormIndex > 0) {
            this.currentFormIndex--;
        }
    }

    // -------------------- IC lookup (step 1) --------------------

    /** Hard-blocking lookup results (blacklist / other status / legacy match). */
    get icHardBlocked(): boolean {
        return (
            this.icCheckStatus === 'blacklisted' ||
            this.icCheckStatus === 'other_status' ||
            this.icCheckStatus === 'possible_legacy_match'
        );
    }

    /**
     * True when progression must be blocked. A transport 'error' also blocks:
     * a backend outage must never fail open, or a returning worker would be
     * registered as a duplicate.
     */
    get icBlocked(): boolean {
        return this.icHardBlocked || this.icCheckStatus === 'error';
    }

    /** Step-1 Next is disabled while blocked or while a lookup is in flight. */
    get icNextDisabled(): boolean {
        return this.icBlocked || this.icCheckStatus === 'checking';
    }

    /**
     * Current IC input in canonical identity form: uppercase with every
     * non-alphanumeric character (dashes, spaces, punctuation) stripped, matching
     * the backend's canonicalization. 001100-00-1111 === 001100001111, so adding
     * or removing dashes is the SAME resolved value (no forced re-check and no
     * false stale-discard).
     */
    private normalizedIc(): string {
        return (this.registrationForm.get('id_number')?.value || '')
            .toString()
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '');
    }

    /**
     * True only when the lookup has RESOLVED for the CURRENT IC value with a
     * status that allows the flow to continue (fresh or active-resubmission).
     */
    private isIcResolvedForCurrentValue(): boolean {
        const ic = this.normalizedIc();
        return (
            !!ic &&
            ic === this.lastCheckedIc &&
            (this.icCheckStatus === 'not_found' ||
                this.icCheckStatus === 'active' ||
                this.icCheckStatus === 'pending' ||
                this.icCheckStatus === 'rejected')
        );
    }

    /** Debounced blur handler for the IC field. Empty IC: silent no-op. */
    onIcBlur(): void {
        const idType = this.registrationForm.get('id_type')?.value;
        if (!idType || !this.normalizedIc()) {
            // Only the explicit Check button surfaces the "enter IC first" toast.
            return;
        }
        if (this.icBlurDebounce) {
            clearTimeout(this.icBlurDebounce);
        }
        this.icBlurDebounce = setTimeout(() => this.checkIc(), 500);
    }

    /**
     * Reset the IC state when the user edits the ID after a check. If the form
     * was prefilled from an ACTIVE record, wipe ALL prefilled data so person A's
     * details can never be submitted under person B's NRIC.
     */
    onIcInputChange(): void {
        const idNumber = this.normalizedIc();
        if (idNumber !== this.lastCheckedIc) {
            const hadPrefill = this.isResubmission;
            this.icCheckStatus = 'idle';
            this.icCheckMessageKey = '';
            this.isResubmission = false;
            this.resubmitEmployeeId = null;
            if (hadPrefill) {
                this.resetFormToDefaults();
            }
        }
    }

    /**
     * Runs the IC lookup (explicit Check button, debounced blur, or the step-1
     * Next gate). When `andThen` is provided it is invoked ONLY if the lookup
     * resolves to a status that allows continuing (not_found / active) for the
     * current IC value.
     */
    checkIc(andThen?: () => void): void {
        const idType = this.registrationForm.get('id_type')?.value;
        const idNumber = this.normalizedIc();

        if (!idType || !idNumber) {
            this._messageService.error(
                this.getLocalizedText('ic_check.enterIcFirst')
            );
            return;
        }

        // A lookup is already in flight.
        if (this.icCheckStatus === 'checking') {
            return;
        }

        // Already resolved for this exact value: reuse the result.
        if (
            idNumber === this.lastCheckedIc &&
            this.icCheckStatus !== 'idle' &&
            this.icCheckStatus !== 'error'
        ) {
            if (andThen && this.isIcResolvedForCurrentValue()) {
                andThen();
            }
            return;
        }

        this.lastCheckedIc = idNumber;
        this.icCheckStatus = 'checking';
        this.icCheckMessageKey = '';
        this.isResubmission = false;
        this.resubmitEmployeeId = null;

        this._partTimerManagementService
            .lookupByNric(idNumber)
            .pipe(take(1), takeUntil(this._unsubscribeAll))
            .subscribe((data) => {
                // Discard stale responses: the user edited the IC while this
                // lookup was in flight. Processing it would prefill/block/advance
                // for the OLD value, so drop it and stay unresolved for the new
                // value (which forces a fresh check before advancing).
                if (this.normalizedIc() !== idNumber) {
                    this.icCheckStatus = 'idle';
                    return;
                }
                if (!data) {
                    // Fail CLOSED: block progression until a retry succeeds.
                    this.icCheckStatus = 'error';
                    this.icCheckMessageKey = 'ic_check.error';
                    return;
                }
                this.handleLookupResult(data);
                if (andThen) {
                    if (this.isIcResolvedForCurrentValue()) {
                        andThen();
                    } else if (this.icBlocked) {
                        this._messageService.error(
                            this.getLocalizedText(this.icCheckMessageKey)
                        );
                    }
                }
            });
    }

    /**
     * Restores every control to its initial default (as built in initForm),
     * keeping only the identity fields the user is currently typing.
     */
    private resetFormToDefaults(): void {
        const keepIdType = this.registrationForm.get('id_type')?.value ?? '';
        const keepIdNumber =
            this.registrationForm.get('id_number')?.value ?? '';

        this.registrationForm.patchValue({
            client_name: '',
            contact_email: '',
            contact_number: '',
            date_of_birth: null,
            gender: '',
            unit_number: '',
            residential_address: '',
            postal_code: '',
            nationality: '',
            pr_start_date: null,
            id_type: keepIdType,
            id_number: keepIdNumber,
            passport_expiry: null,
            place_of_birth: '',
            age: '',
            race: '',
            religion: '',
            marital_status: '',
            kin_name: '',
            kin_contact: '',
            bank_name: '',
            bank_acc: '',
            where_know_us: '',
            type_of_employment: 'parttime',
            position_applied: '',
            earliest_availability: null,
            fully_vaccinated: 0,
            highest_education: '',
            school: '',
            country: 'Singapore',
            date_start: null,
            date_grad: null,
            high_standard: '',
            relatives_in_company: 0,
            relatives_info: '',
            declaration: false,
        });
        this.registrationForm.get('qna')?.patchValue({
            where_know_us: '',
            fully_vaccinated: 0,
            relatives_in_company: 0,
            relatives_info: '',
            any_illness: 0,
            physical: 0,
            mental_illness: 0,
            medication: 0,
            medication_details: '',
            found_guilty: 0,
            any_investigation: 0,
            objection_undergo: 0,
            alcohol_drug_addiction: 0,
            undischarged_bankrupt: 0,
            dismissed_or_suspended: 0,
            business_undertaking: 0,
        });

        this.workingExps.clear();
        this.addWorkingExperience();
        this.employeeCertificates = [];
        this.addressComponents = null;
        this.temporaryAddress = '';
        this.postalCodeValid = false;
        // Person A's drawn signature must not survive into person B's flow.
        if (this.signaturePad) {
            this.signaturePad.clear();
        }

        this.registrationForm.markAsPristine();
        this.registrationForm.markAsUntouched();
        this._changeDetectorRef.detectChanges();
    }

    /**
     * Prefill + resubmission setup shared by the active, pending and rejected lookup
     * outcomes: switch to resubmission mode, carry the employee_id so submit routes to
     * the resubmit endpoint, and patch the existing record onto the form. Pending and
     * rejected are non-blocking informational states (the applicant may proceed and
     * submit), differing from active only by the banner message shown.
     */
    private enterResubmission(
        data: any,
        status: 'active' | 'pending' | 'rejected',
        messageKey: string
    ): void {
        this.icCheckStatus = status;
        this.icCheckMessageKey = messageKey;
        this.isResubmission = true;
        this.resubmitEmployeeId =
            data.employee_id != null ? data.employee_id.toString() : null;
        if (data.prefill) {
            this.applyPrefill(data.prefill);
        }
    }

    private handleLookupResult(data: any): void {
        const status = (data.match_status || '').toString().toUpperCase();

        switch (status) {
            case 'ACTIVE':
                // Existing active record: prefill + resubmission (unchanged).
                this.enterResubmission(data, 'active', 'ic_check.foundActive');
                break;
            case 'PENDING_RESUBMIT':
                // Pending applicant fixing their own application: prefill + resubmit.
                this.enterResubmission(
                    data,
                    'pending',
                    'ic_check.pendingResubmit'
                );
                break;
            case 'REJECTED_RESUBMIT':
                // Previously rejected: prefill so they can correct and re-apply.
                this.enterResubmission(
                    data,
                    'rejected',
                    'ic_check.rejectedResubmit'
                );
                break;
            case 'BLACKLISTED':
                this.icCheckStatus = 'blacklisted';
                this.icCheckMessageKey = 'ic_check.blacklisted';
                break;
            case 'OTHER_STATUS':
                this.icCheckStatus = 'other_status';
                this.icCheckMessageKey = this.otherStatusMessageKey(
                    data.status
                );
                break;
            case 'POSSIBLE_LEGACY_MATCH':
                this.icCheckStatus = 'possible_legacy_match';
                this.icCheckMessageKey = 'ic_check.possibleLegacy';
                break;
            case 'NOT_FOUND':
            default:
                // Fresh registration - continue silently.
                this.icCheckStatus = 'not_found';
                this.icCheckMessageKey = '';
                break;
        }
    }

    private otherStatusMessageKey(status: string): string {
        switch ((status || '').toString().toLowerCase()) {
            case 'pending':
                return 'ic_check.pending';
            case 'rejected':
                return 'ic_check.rejected';
            case 'discharge':
                return 'ic_check.discharge';
            default:
                return 'ic_check.otherStatus';
        }
    }

    private toDate(value: any): Date | null {
        if (!value) return null;
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }

    /**
     * Returns the date only if it is today or later; past dates become null so
     * the min-date validator (startDateFilter) cannot trap the user on prefill.
     */
    private futureOrNull(d: Date | null): Date | null {
        if (!d) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return d >= today ? d : null;
    }

    /**
     * Converts a backend date (yyyy-MM-dd string) to the ADAPTER-NATIVE type.
     * The app's Material DateAdapter is the LuxonDateAdapter (app.config.ts), so
     * matDatepicker inputs must be given a luxon DateTime - exactly what the
     * datepicker itself writes back when the user picks a date manually. A plain
     * JS Date is not `isDateInstance` for that adapter and can render as an
     * empty input even though the control holds a value.
     */
    private toAdapterDate(value: any): DateTime | null {
        if (!value) {
            return null;
        }
        const dt =
            typeof value === 'string'
                ? DateTime.fromISO(value)
                : DateTime.fromJSDate(new Date(value));
        return dt.isValid ? dt : null;
    }

    /**
     * Patches every matDatepicker-bound control with a luxon DateTime. Runs
     * AFTER the main patchValue so the nationality/id_type valueChanges
     * subscriptions (which clear pr_start_date / passport_expiry) cannot wipe
     * these values. The method name doubles as a greppable build marker.
     */
    private applyPrefillDates(prefill: any): { dob: DateTime | null } {
        const dob = this.toAdapterDate(prefill.date_of_birth);
        const earliest = this.futureOrNull(
            this.toDate(prefill.earliest_availability)
        );
        this.registrationForm.patchValue({
            date_of_birth: dob,
            passport_expiry: this.toAdapterDate(prefill.passport_expiry),
            pr_start_date: this.toAdapterDate(prefill.pr_start_date),
            earliest_availability: earliest
                ? DateTime.fromJSDate(earliest)
                : null,
            date_start: this.toAdapterDate(prefill.edu_start),
            date_grad: this.toAdapterDate(prefill.edu_grad),
        });
        return { dob };
    }

    /**
     * Select option values per control, mirroring the template EXACTLY. Stored
     * values are matched tolerantly (case/punctuation-insensitive) and mapped to
     * the exact option value so prefilled selects always display.
     */
    private static readonly PREFILL_OPTION_VALUES: {
        [control: string]: string[];
    } = {
        gender: ['Male', 'Female'],
        nationality: [
            'Singaporean',
            'Singapore PR',
            'Malaysian',
            'Chinese',
            'OTHER',
        ],
        id_type: ['NRIC', 'FIN', 'MY_IC', 'CN_IC', 'Passport', 'OTHER'],
        race: ['chinese', 'malay', 'indian', 'eurasian', 'other'],
        religion: [
            'buddhist',
            'christian',
            'catholic',
            'muslim',
            'hindu',
            'sikh',
            'freethinker',
            'other',
        ],
        marital_status: ['single', 'married', 'divorced', 'widowed'],
        where_know_us: [
            'fastjob',
            'newspaperAdvertisement',
            'onlineJobBoard',
            'companyWebsite',
            'socialMedia',
            'employeeReferral',
            'localAdvertisement',
            'schoolUniversity',
            'wordOfMouth',
            'recruitmentAgency',
            'other',
        ],
        position_applied: [
            'Food Operator',
            'Catering Assistant',
            'Logistics Assistant',
            'Warehouse Assistant',
            'Retort Packing',
            'MK Admin',
            'Project Logistics',
            'FnB Service',
            'others',
        ],
        highest_education: [
            'Primary',
            'Secondary',
            'Polytechnic / College',
            'University',
            'None',
        ],
        high_standard: [
            'PSLE',
            'GCE N-Level',
            'GCE O-Level',
            'SPM',
            'GCE A-Level',
            'Diploma / Specialist',
            'Nitec / Higher Nitec',
            'STPM',
            "Bachelor's Degree",
            'Graduate Diploma',
            'Master Degree / MBA',
            'PhD',
        ],
    };

    /**
     * Maps a stored value onto the control's EXACT option value, tolerating
     * case/punctuation differences ("Chinese" -> "chinese", "Free Thinker" ->
     * "freethinker"). Blank and "-" placeholders become '' (shows "Please
     * select"); unknown values pass through unchanged.
     */
    private matchOption(control: string, value: any): string {
        if (value == null) {
            return '';
        }
        const raw = value.toString().trim();
        if (!raw || raw === '-') {
            return '';
        }
        const options = PartTimerComponent.PREFILL_OPTION_VALUES[control];
        if (!options) {
            return raw;
        }
        const canon = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
        const hit = options.find((o) => canon(o) === canon(raw));
        return hit ?? raw;
    }

    /** Maps an ACTIVE prefill payload onto the form controls across all steps. */
    private applyPrefill(prefill: any): void {
        if (!prefill) {
            // TEMP DIAGNOSTIC (remove after prefill issue resolved)
            console.error(
                '[PREFILL-DEBUG] applyPrefill received EMPTY prefill - service delivered nothing'
            );
            return;
        }
        // TEMP DIAGNOSTIC (remove after prefill issue resolved)
        console.log(
            '[PREFILL-DEBUG] start | keys:',
            Object.keys(prefill).length,
            '| gender:',
            prefill.gender,
            '| dob:',
            prefill.date_of_birth,
            '| pob:',
            prefill.place_of_birth,
            '| controls exist:',
            !!this.registrationForm.get('gender'),
            !!this.registrationForm.get('date_of_birth'),
            !!this.registrationForm.get('place_of_birth')
        );
        try {
            // Personal + contact + address. Select controls go through matchOption so
            // the value matches an <option> EXACTLY and "-" placeholders never land in
            // a select. Date controls are patched separately in applyPrefillDates()
            // with adapter-native luxon DateTime values.
            this.registrationForm.patchValue({
                client_name: prefill.client_name ?? '',
                contact_email: prefill.contact_email ?? '',
                contact_number: prefill.contact_number ?? '',
                nationality: this.matchOption(
                    'nationality',
                    prefill.nationality
                ),
                id_type:
                    this.matchOption('id_type', prefill.id_type) ||
                    this.registrationForm.get('id_type')?.value,
                id_number:
                    prefill.nric ??
                    this.registrationForm.get('id_number')?.value,
                gender: this.matchOption('gender', prefill.gender),
                place_of_birth: prefill.place_of_birth ?? '',
                race: this.matchOption('race', prefill.race),
                religion: this.matchOption('religion', prefill.religion),
                marital_status: this.matchOption(
                    'marital_status',
                    prefill.marital_status
                ),
                unit_number: prefill.street_number ?? '',
                residential_address: prefill.residential_address ?? '',
                postal_code: prefill.postal_code ?? '',
                // Kin + bank
                kin_name: prefill.kin_name ?? '',
                kin_contact: prefill.kin_contact ?? '',
                bank_name: prefill.bank_name ?? '',
                bank_acc: prefill.bank_acc ?? '',
                // Employment
                type_of_employment: prefill.type_of_employment ?? 'parttime',
                position_applied: this.matchOption(
                    'position_applied',
                    prefill.position_applied
                ),
                // Education
                highest_education: this.matchOption(
                    'highest_education',
                    prefill.highest_education
                ),
                school: prefill.school ?? '',
                country: prefill.edu_country ?? 'Singapore',
                high_standard: this.matchOption(
                    'high_standard',
                    prefill.high_standard
                ),
            });

            // All matDatepicker controls, patched AFTER the block above so the
            // nationality/id_type subscriptions cannot clear them (build marker:
            // applyPrefillDates).
            const { dob } = this.applyPrefillDates(prefill);

            // Age derives from DOB: patching date_of_birth triggers the valueChanges
            // subscription that computes and sets the age control. Use the stored age
            // only when there is no DOB to derive from.
            if (!dob && prefill.age != null && prefill.age !== '') {
                this.registrationForm.get('age')?.setValue(prefill.age);
            }

            // QnA + the mirrored top-level controls
            const qna = prefill.qna || {};
            this.registrationForm.patchValue({
                where_know_us: this.matchOption(
                    'where_know_us',
                    qna.where_know_us
                ),
                fully_vaccinated: qna.fully_vaccinated ?? 0,
                relatives_in_company: qna.relatives_in_company ?? 0,
                relatives_info: qna.relatives_info ?? '',
            });
            this.registrationForm.get('qna')?.patchValue({
                where_know_us: this.matchOption(
                    'where_know_us',
                    qna.where_know_us
                ),
                fully_vaccinated: qna.fully_vaccinated ?? 0,
                relatives_in_company: qna.relatives_in_company ?? 0,
                relatives_info: qna.relatives_info ?? '',
                any_illness: qna.any_illness ?? 0,
                physical: qna.physical ?? 0,
                mental_illness: qna.mental_illness ?? 0,
                medication: qna.medication ?? 0,
                medication_details: qna.medication_details ?? '',
                found_guilty: qna.found_guilty ?? 0,
                any_investigation: qna.any_investigation ?? 0,
                objection_undergo: qna.objection_undergo ?? 0,
                alcohol_drug_addiction: qna.alcohol_drug_addiction ?? 0,
                undischarged_bankrupt: qna.undischarged_bankrupt ?? 0,
                dismissed_or_suspended: qna.dismissed_or_suspended ?? 0,
                business_undertaking: qna.business_undertaking ?? 0,
            });

            // Address components (so submit sends the stored address + postal code)
            this.temporaryAddress = prefill.residential_address ?? '';
            // The visible address field belongs to the autocomplete WIDGET, not the
            // form control: push the text into the widget the same way the reactive
            // binding on the edit-employee screen does (via its writeValue).
            if (this.temporaryAddress) {
                this.addressAutocomplete?.writeValue(this.temporaryAddress);
            }
            this.postalCodeValid = !!prefill.postal_code;
            this.addressComponents = {
                formatted_address:
                    prefill.formatted_address ??
                    prefill.residential_address ??
                    null,
                location_lat:
                    prefill.location_lat != null
                        ? prefill.location_lat.toString()
                        : null,
                location_lng:
                    prefill.location_lng != null
                        ? prefill.location_lng.toString()
                        : null,
                street_number: prefill.street_number ?? null,
                street: prefill.street ?? null,
                city: prefill.city ?? null,
                state: prefill.state ?? null,
                country: prefill.country ?? null,
                country_code: null,
                place_id: prefill.place_id ?? null,
            };

            // Working experience array
            if (
                Array.isArray(prefill.working_exps) &&
                prefill.working_exps.length
            ) {
                this.workingExps.clear();
                prefill.working_exps.forEach((exp: any) => {
                    this.workingExps.push(
                        this._formBuilder.group({
                            previous_company_name: [
                                exp.previous_company_name ?? '',
                            ],
                            position: [exp.position ?? ''],
                            date_start: [this.toAdapterDate(exp.date_start)],
                            date_left: [this.toAdapterDate(exp.date_left)],
                            reason_for_leaving: [exp.reason_for_leaving ?? ''],
                        })
                    );
                });
            }

            // Certificates metadata (existing certs shown as already-uploaded)
            if (Array.isArray(prefill.certificates)) {
                this.employeeCertificates = prefill.certificates.map(
                    (c: any) => ({
                        id: c.id != null ? c.id.toString() : undefined,
                        certificate_type_id:
                            c.certificate_type_id != null
                                ? c.certificate_type_id.toString()
                                : '',
                        certificate_url: c.certificate_url,
                        file: c.certificate_url || '',
                        has_expiry: !!c.has_expiry,
                        expiry_date: c.expiry_date || undefined,
                    })
                );
            }

            this._changeDetectorRef.detectChanges();
            // TEMP DIAGNOSTIC (remove after prefill issue resolved)
            console.log('[PREFILL-DEBUG] done | control values now:', {
                gender: this.registrationForm.get('gender')?.value,
                date_of_birth: String(
                    this.registrationForm.get('date_of_birth')?.value ?? ''
                ),
                place_of_birth:
                    this.registrationForm.get('place_of_birth')?.value,
                unit_number: this.registrationForm.get('unit_number')?.value,
                age: this.registrationForm.get('age')?.value,
                contact_number:
                    this.registrationForm.get('contact_number')?.value,
                residential_address: this.registrationForm.get(
                    'residential_address'
                )?.value,
            });
        } catch (e) {
            // TEMP DIAGNOSTIC (remove after prefill issue resolved)
            console.error('[PREFILL-DEBUG] applyPrefill THREW:', e);
            throw e;
        }
    }

    getStepClass(stepIndex: number): string {
        if (stepIndex < this.currentFormIndex) {
            return 'bg-green-600 text-white';
        } else if (stepIndex === this.currentFormIndex) {
            return 'bg-blue-600 text-white';
        } else {
            return 'bg-gray-100 text-gray-900';
        }
    }

    getStepContent(stepIndex: number): string {
        if (stepIndex < this.currentFormIndex) {
            return '✓';
        } else {
            return (stepIndex + 1).toString();
        }
    }

    clearSignature() {
        this.signaturePad.clear();
    }

    addWorkingExperience() {
        const workExpGroup = this._formBuilder.group({
            previous_company_name: [''],
            position: [''],
            date_start: [null],
            date_left: [null],
            reason_for_leaving: [''],
        });

        this.workingExps.push(workExpGroup);
    }

    removeWorkingExperience(index: number) {
        if (this.workingExps.length > 1) {
            this.workingExps.removeAt(index);
        }
    }

    /** Same legacy no-op as onDateInput2 - see the comment above it. */
    onDateInputAllForm(
        _value: unknown,
        _index: number,
        _fieldName: string
    ): void {
        // Intentionally empty: formControlName already synced the control.
    }

    isWorkingExpFieldInvalid(index: number, fieldName: string): boolean {
        const control = this.workingExps.at(index).get(fieldName);

        // Skip validation if the entire entry is empty
        const expGroup = this.workingExps.at(index);
        const hasAnyValue =
            expGroup.get('previous_company_name').value ||
            expGroup.get('position').value ||
            expGroup.get('date_start').value ||
            expGroup.get('date_left').value ||
            expGroup.get('reason_for_leaving').value;

        // Only validate if any field in this experience has data
        if (!hasAnyValue) {
            return false;
        }

        return control.invalid && (control.dirty || control.touched);
    }

    private validateWorkExperiences(): boolean {
        if (this.workingExps.length === 0) {
            return true; // No work experiences is valid
        }

        let isValid = true;

        this.workingExps.controls.forEach((expGroup: FormGroup, index) => {
            // Check if any field has value
            const hasAnyValue =
                expGroup.get('previous_company_name').value ||
                expGroup.get('position').value ||
                expGroup.get('date_start').value ||
                expGroup.get('date_left').value ||
                expGroup.get('reason_for_leaving').value;

            // If any field has value, all fields must have values
            if (hasAnyValue) {
                Object.keys(expGroup.controls).forEach((key) => {
                    const control = expGroup.get(key);
                    if (!control.value) {
                        control.markAsTouched();
                        control.markAsDirty();
                        isValid = false;
                    }
                });
            }
        });

        return isValid;
    }

    handleSubmit(contract: File) {
        // Backstop: never post without a resolved IC check for the current value.
        if (!this.isIcResolvedForCurrentValue()) {
            this._messageService.error(
                this.getLocalizedText('ic_check.verifyRequired')
            );
            this.submitting = false;
            this.isProceedToBackend = false;
            return;
        }

        if (this.registrationForm.invalid) {
            this._messageService.error(
                this.getLocalizedText('validation.fillAllFields')
            );
            return;
        }

        if (this.signaturePad.isEmpty()) {
            this._messageService.error(
                this.getLocalizedText('validation.signatureRequired')
            );
            return;
        }

        this.isProceedToBackend = true;
        this.submitting = true;

        // Update QnA with the main form values before capturing snapshot
        this.registrationForm.get('qna')?.patchValue({
            where_know_us: this.registrationForm.get('where_know_us')?.value,
            fully_vaccinated:
                this.registrationForm.get('fully_vaccinated')?.value,
            relatives_in_company: this.registrationForm.get(
                'relatives_in_company'
            )?.value,
            relatives_info: this.registrationForm.get('relatives_info')?.value,
        });

        // Get form values
        const formValues = this.registrationForm.getRawValue();

        const dobTimestamp = Math.floor(
            new Date(formValues.date_of_birth).getTime() / 1000
        );
        const availabilityTimestamp = Math.floor(
            new Date(formValues.earliest_availability).getTime() / 1000
        );
        const passportExpiryTime = Math.floor(
            new Date(formValues.passport_expiry).getTime() / 1000
        );

        const eduStartTimestamp = Math.floor(
            new Date(formValues.date_start).getTime() / 1000
        );
        const eduEndTimestamp = Math.floor(
            new Date(formValues.date_grad).getTime() / 1000
        );

        const pr_start_date = Math.floor(
            new Date(formValues.pr_start_date).getTime() / 1000
        );

        // Validate work experiences
        if (!this.validateWorkExperiences()) {
            this._messageService.error(
                'Please complete all fields in work experience or remove it'
            );
            return;
        }

        // Convert working experience dates (to seconds)
        const convertedWorkingExps = formValues.working_exps
            .map((exp) => {
                // Check if any fields are filled in this experience entry
                const hasData =
                    exp.previous_company_name ||
                    exp.position ||
                    exp.date_start ||
                    exp.date_left ||
                    exp.reason_for_leaving;

                // If no data was entered, skip this entry
                if (!hasData) {
                    return null;
                }

                // Validate data completeness for entries with data
                if (
                    hasData &&
                    (!exp.previous_company_name ||
                        !exp.position ||
                        !exp.date_start ||
                        !exp.date_left ||
                        !exp.reason_for_leaving)
                ) {
                    // If partially filled, show error
                    this._messageService.error(
                        'Please complete all fields in work experience or remove it'
                    );
                    return; // This will cause the map to return undefined for this entry
                }

                return {
                    ...exp,
                    date_start: exp.date_start
                        ? Math.floor(
                              new Date(exp.date_start).getTime() / 1000
                          ).toString()
                        : '',
                    date_left: exp.date_left
                        ? Math.floor(
                              new Date(exp.date_left).getTime() / 1000
                          ).toString()
                        : '',
                };
            })
            .filter((exp) => exp !== null);

        // Get signature as base64 string
        const signatureData = this.signaturePad.toDataURL();

        this.registrationForm.disable();

        // Log all form data
        console.group('Form Submission Data');
        console.log('Personal Information:', {
            client_name: formValues.client_name,
            contact_email: formValues.contact_email,
            contact_number: formValues.contact_number,
            nric: formValues.nric,
            date_of_birth: formValues.date_of_birth,
            gender: formValues.gender,
        });

        console.log('Address Information:', {
            residential_address: formValues.residential_address,
            postal_code: formValues.postal_code,
        });

        console.log('Emergency Contact:', {
            kin_name: formValues.kin_name,
            kin_contact: formValues.kin_contact,
        });

        console.log('Bank Details:', {
            bank_name: formValues.bank_name,
            bank_acc: formValues.bank_acc,
        });

        console.log('Employment Details:', {
            highest_education: formValues.highest_education,
            type_of_employment: formValues.type_of_employment,
            position_applied: formValues.position_applied,
            earliest_availability: formValues.earliest_availability,
        });

        console.log('Working Experience:', formValues.working_exps);

        console.log('QnA Responses:', formValues.qna);

        console.log('Converted Timestamps:', {
            date_of_birth: dobTimestamp,
            earliest_availability: availabilityTimestamp,
        });

        console.log('Signature Data:', signatureData.substring(0, 100) + '...'); // Show first 100 chars only
        console.groupEnd();

        console.log(this.requiresDocument);

        // Extract address components or use default values if not available
        const unitNumber = formValues.unit_number;
        const addressData = this.addressComponents || {
            formatted_address: this.temporaryAddress || null,
            location_lat: null,
            location_lng: null,
            street_number: null,
            street: null,
            city: null,
            state: null,
            country: null,
            country_code: null,
            place_id: null,
        };

        // Override street_number with unit_number if provided
        if (unitNumber) {
            addressData.street_number = unitNumber;
        }

        // Clean the residential address if unit number is provided
        let cleanedResidentialAddress = formValues.residential_address;
        if (unitNumber) {
            // Remove any existing number/unit at the beginning of the address
            // This regex matches patterns like "50, 52, " or "52, " at the start
            const cleanedAddress = formValues.residential_address.replace(
                /^[\d\-#A-Za-z]+,\s*(\d+,\s*)?/,
                ''
            );
            cleanedResidentialAddress = `${unitNumber}, ${cleanedAddress}`;
        }

        this._partTimerManagementService
            .createPT(
                formValues.client_name,
                formValues.contact_email,
                formValues.contact_number,
                formValues.nationality,
                formValues.id_type,
                formValues.id_number,
                passportExpiryTime.toString(),
                dobTimestamp.toString(),
                formValues.gender,
                cleanedResidentialAddress,
                formValues.postal_code,
                // Add the address data from Google Places
                addressData.formatted_address,
                addressData.location_lat,
                addressData.location_lng,
                addressData.street_number,
                addressData.street,
                addressData.city,
                addressData.state,
                addressData.country,
                addressData.country_code,
                addressData.place_id,
                formValues.kin_name,
                formValues.kin_contact,
                formValues.bank_name,
                formValues.bank_acc,
                formValues.highest_education,
                formValues.school,
                formValues.country,
                eduStartTimestamp.toString(),
                eduEndTimestamp.toString(),
                formValues.high_standard,
                formValues.type_of_employment,
                formValues.position_applied,
                pr_start_date.toString(),
                formValues.place_of_birth,
                formValues.age,
                formValues.race,
                formValues.religion,
                formValues.marital_status,
                availabilityTimestamp.toString(),
                signatureData,
                this.employeeCertificates,
                contract,
                formValues.qna,
                convertedWorkingExps,
                this.requiresDocument,
                this.isResubmission && this.resubmitEmployeeId
                    ? this.resubmitEmployeeId
                    : undefined
            )
            .pipe(take(1), takeUntil(this._unsubscribeAll))
            .subscribe(
                (result) => {
                    if (result) {
                        // Handle successful submission
                        console.log('Registration successful');
                        this._messageService.success(
                            this.isResubmission
                                ? this.getLocalizedText(
                                      'ic_check.resubmitSuccess'
                                  )
                                : 'Submitted Successfully'
                        );
                        try {
                            this.registrationForm.enable();
                        } catch (e) {
                            console.error('Error enabling form:', e);
                        }
                        this.submitting = false;
                        setTimeout(() => {
                            window.location.reload();
                        }, 1500);
                    } else {
                        // Backend returned false (error was already shown by service)
                        // Reset all loading states to allow resubmission
                        try {
                            this.registrationForm.enable();
                        } catch (e) {
                            console.error('Error enabling form:', e);
                        }
                        this.submitting = false;
                        this.isProceedToBackend = false;
                    }
                },
                (error) => {
                    console.error('Registration failed:', error);
                    // Extract error message from response
                    let errorMessage = 'Registration failed. Please try again.';
                    if (error?.error?.message) {
                        errorMessage = error.error.message;
                    } else if (error?.message) {
                        errorMessage = error.message;
                    }
                    this._messageService.error(errorMessage);
                    // Reset all loading states to allow resubmission
                    try {
                        this.registrationForm.enable();
                    } catch (e) {
                        console.error('Error enabling form:', e);
                    }
                    this.submitting = false;
                    this.isProceedToBackend = false;
                }
            );
    }

    ngOnDestroy(): void {
        if (this.icBlurDebounce) {
            clearTimeout(this.icBlurDebounce);
        }
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    private getCurrentFormControls(): string[] {
        switch (this.currentFormIndex) {
            case 0: {
                // Step 1: personal details (contains NRIC/IC)
                const personalControls = [
                    'client_name',
                    'nationality',
                    'id_type',
                    'id_number',
                    'contact_number',
                    'date_of_birth',
                    'gender',
                    'unit_number',
                    'residential_address',
                    'postal_code',
                    'contact_email',
                    'place_of_birth',
                    'race',
                    'religion',
                    'marital_status',
                ];

                // Add passport_expiry conditionally if ID type is Passport
                if (
                    this.registrationForm.get('id_type')?.value === 'Passport'
                ) {
                    personalControls.push('passport_expiry');
                }

                // pr_start_date becomes required (via valueChanges) only when
                // the applicant is a Singapore PR, so gate it the same way.
                if (
                    this.registrationForm.get('nationality')?.value ===
                    'Singapore PR'
                ) {
                    personalControls.push('pr_start_date');
                }

                return personalControls;
            }
            case 1: // Step 2: employment / application details
                return [
                    'where_know_us',
                    'type_of_employment',
                    'position_applied',
                    'earliest_availability',
                    'fully_vaccinated',
                ];
            case 2: // Form 3
                return ['kin_name', 'kin_contact', 'bank_name', 'bank_acc'];
            case 3: // Form 4
                const controls = [
                    'highest_education',
                    'relatives_in_company',
                    'working_exps',
                ];
                if (this.showRelativesInfo()) {
                    controls.push('relatives_info');
                }
                return controls;
            // ... other cases for other forms
            default:
                return [];
        }
    }

    isFieldInvalid(formControlName: string): boolean {
        const control = this.registrationForm.get(formControlName);
        if (!control) return false;

        return control.invalid && (control.dirty || control.touched);
    }

    public onBankSearch(term: string): void {
        const lower = (term || '').toLowerCase().trim();
        if (!lower) {
            this.filteredBankList = [...this.bankList];
        } else {
            this.filteredBankList = this.bankList.filter((bank) =>
                bank.toLowerCase().includes(lower)
            );
        }
        this.showBankDropdown = true;
    }

    public selectBank(bank: string): void {
        this.registrationForm.get('bank_name')?.setValue(bank);
        this.showBankDropdown = false;
    }

    public openBankDropdown(): void {
        this.showBankDropdown = true;
        this.filteredBankList = [...this.bankList];
    }

    public closeBankDropdown(): void {
        // Delay to allow click on dropdown option to register
        setTimeout(() => {
            this.showBankDropdown = false;
            // If user typed something that doesn't match a bank exactly, clear it
            const currentValue =
                this.registrationForm.get('bank_name')?.value || '';
            if (currentValue && !this.bankList.includes(currentValue)) {
                this.registrationForm.get('bank_name')?.setValue('');
            }
        }, 200);
    }

    onCertificatesChange(certificates: EmployeeCertificate[]): void {
        this.employeeCertificates = certificates;
        // Mark form as dirty to track changes
        this.registrationForm.markAsDirty();
    }

    private dateToTimestamp(dateString: string): string {
        return new Date(dateString).getTime().toString();
    }

    getCurrentDate(): string {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    onDateInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const value = input.value;

        // Convert yyyy-MM-dd to dd/MM/yyyy for form value
        if (value) {
            const [year, month, day] = value.split('-');
            const formattedDate = `${day}/${month}/${year}`;
            this.registrationForm
                .get('earliest_availability')
                ?.setValue(formattedDate, { emitEvent: false });
        }
    }

    /**
     * Legacy handler from the native <input type="date"> era. The templates
     * pass $event.target.value (a luxon DateTime or null) from matDatepicker
     * (dateChange), while this method was typed to receive an Event - reading
     * event.target.value on a DateTime crashed every calendar pick (NG date
     * handlers audit, 2026-07-10). The matDatepicker + formControlName +
     * LuxonDateAdapter already write the picked/typed value into the control,
     * so no conversion is needed here at all; kept as a no-op to avoid
     * touching 8 template call sites.
     */
    onDateInput2(_value: unknown): void {
        // Intentionally empty: formControlName already synced the control.
    }

    /** Same legacy no-op as onDateInput2 - see the comment above it. */
    onDateInputAll(_value: unknown, _formName: string): void {
        // Intentionally empty: formControlName already synced the control.
    }

    async generateDocument() {
        // Backstop: never open the contract dialog without a resolved IC check
        // for the current IC value (re-asserted in handleSubmit as well).
        if (!this.isIcResolvedForCurrentValue()) {
            this._messageService.error(
                this.getLocalizedText('ic_check.verifyRequired')
            );
            return;
        }

        // Validate declaration checkbox - must be checked
        const declarationControl = this.registrationForm.get('declaration');
        if (!declarationControl?.value) {
            this._messageService.error(
                this.getLocalizedText('validation.declarationRequired')
            );
            declarationControl?.markAsTouched();
            return;
        }

        // Validate signature
        if (this.signaturePad.isEmpty()) {
            this._messageService.error(
                this.getLocalizedText('validation.signatureRequired')
            );
            return;
        }

        // Validate form before proceeding
        if (this.registrationForm.invalid) {
            this._messageService.error(
                this.getLocalizedText('validation.fillAllFields')
            );
            this.registrationForm.markAllAsTouched();
            return;
        }

        try {
            const formData = this.registrationForm.value;
            const signatureImage = this.signaturePad.toDataURL('image/png');

            const isPromoter = formData.position_applied
                .toLowerCase()
                .includes('promoter');

            if (isPromoter) {
                // Show promoter agreement dialog
                const unitNumber = formData.unit_number;
                let fullAddress = formData.residential_address;

                // If unit number is provided, replace any existing number at the start
                if (unitNumber) {
                    const cleanedAddress = formData.residential_address.replace(
                        /^[\d\-#A-Za-z]+,\s*(\d+,\s*)?/,
                        ''
                    );
                    fullAddress = `${unitNumber}, ${cleanedAddress}`;
                }

                const dialogRef = this.dialog.open(PromoterServicesComponent, {
                    width: '80vw',
                    height: '90vh',
                    data: {
                        contractName: formData.client_name,
                        icNumber: formData.id_number,
                        address: fullAddress,
                        signature: signatureImage,
                        currentDate: new Date().toLocaleDateString(),
                    },
                });

                dialogRef.afterClosed().subscribe((result) => {
                    if (result) {
                        const contractFile = new File(
                            [result.pdfBlob],
                            result.fileName,
                            { type: 'application/pdf' }
                        );

                        this.isProceedToBackend = true;
                        this.handleSubmit(contractFile);
                    } else {
                        // Dialog was cancelled - reset states
                        this.submitting = false;
                        this.isProceedToBackend = false;
                    }
                });
            } else {
                // Show food handling agreement dialog
                const unitNumber = formData.unit_number;
                let fullAddress = formData.residential_address;

                if (unitNumber) {
                    const cleanedAddress = formData.residential_address.replace(
                        /^[\d\-#A-Za-z]+,\s*(\d+,\s*)?/,
                        ''
                    );
                    fullAddress = `${unitNumber}, ${cleanedAddress}`;
                }

                const dialogRef = this.dialog.open(
                    FoodHandlingServicesComponent,
                    {
                        width: '80vw',
                        height: '90vh',
                        data: {
                            contractName: formData.client_name,
                            icNumber: formData.id_number,
                            address: fullAddress,
                            signature: signatureImage,
                            currentDate: new Date().toLocaleDateString(),
                            positionApplied: formData.position_applied,
                        },
                    }
                );

                dialogRef.afterClosed().subscribe((result) => {
                    if (result) {
                        const contractFile = new File(
                            [result.pdfBlob],
                            result.fileName,
                            { type: 'application/pdf' }
                        );

                        this.isProceedToBackend = true;
                        this.handleSubmit(contractFile);
                    } else {
                        // Dialog was cancelled - reset states
                        this.submitting = false;
                        this.isProceedToBackend = false;
                    }
                });
            }
        } catch (error) {
            console.error('Error generating document:', error);
            // Reset states on error
            this.submitting = false;
            this.isProceedToBackend = false;
            try {
                this.registrationForm.enable();
            } catch (e) {
                console.error('Error enabling form:', e);
            }
        }
    }

    private calculateAge(birthDate: any): number {
        const today = new Date();
        // Accepts a date string, JS Date, or a luxon DateTime (what the
        // matDatepicker writes and what applyPrefillDates patches).
        const birth: Date =
            birthDate && typeof birthDate.toJSDate === 'function'
                ? birthDate.toJSDate()
                : new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();

        if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birth.getDate())
        ) {
            age--;
        }
        console.log('Calculated age:', age); // Debug log
        return age;
    }

    private ageValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            const age = this.calculateAge(control.value);
            return age < 15 ? { ageInvalid: true } : null;
        };
    }

    private ageMinValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            // If empty, return null (valid)
            if (control.value === '' || control.value === null) {
                return null;
            }

            // If there's a value, check if it's at least 15
            const age = parseInt(control.value);
            return age < 15 ? { minAge: true } : null;
        };
    }

    /**
     * Validates that all added certificates have required fields completed
     * @returns true if all certificates are complete, false if any are incomplete
     */
    private validateCertificateCompleteness(): boolean {
        // If no certificates added, return true (valid - will be caught by other validation if required)
        if (
            !this.employeeCertificates ||
            this.employeeCertificates.length === 0
        ) {
            return true;
        }

        // Check each certificate for completeness
        return this.employeeCertificates.every((cert) => {
            // Certificate must have a type
            const hasType =
                cert.certificate_type_id && cert.certificate_type_id !== '';

            // Certificate must have either a file (new) or certificate_url (existing)
            const hasFileOrUrl = !!(cert.file || cert.certificate_url);

            // If certificate has expiry enabled, expiry_date must be provided
            const hasValidExpiry =
                !cert.has_expiry || (cert.has_expiry && cert.expiry_date);

            return hasType && hasFileOrUrl && hasValidExpiry;
        });
    }

    /**
     * Marks invalid certificate fields as touched to trigger validation error display
     */
    private markInvalidCertificateFieldsAsTouched(): void {
        if (!this.certificatesManager?.certificatesFormArray) {
            return;
        }

        // Iterate through each certificate form group
        this.certificatesManager.certificatesFormArray.controls.forEach(
            (certFormGroup) => {
                const formGroup = certFormGroup as FormGroup;

                // Mark certificate_type_id as touched if empty
                const typeControl = formGroup.get('certificate_type_id');
                if (typeControl && !typeControl.value) {
                    typeControl.markAsTouched();
                }

                // Mark file as touched if empty and no certificate_url exists
                const fileControl = formGroup.get('file');
                const urlControl = formGroup.get('certificate_url');
                if (fileControl && !fileControl.value && !urlControl?.value) {
                    fileControl.markAsTouched();
                }

                // Mark expiry_date as touched if has_expiry is true but no date selected
                const hasExpiryControl = formGroup.get('has_expiry');
                const expiryDateControl = formGroup.get('expiry_date');
                if (
                    hasExpiryControl?.value &&
                    expiryDateControl &&
                    !expiryDateControl.value
                ) {
                    expiryDateControl.markAsTouched();
                }
            }
        );
    }

    // Address methods

    public onAddressChanged(addressData: AddressData): void {
        // Update our form with the formatted address and other fields
        const formValues: any = {
            residential_address: addressData.formattedAddress,
            postal_code: addressData.postalCode || '',
        };

        this.registrationForm.patchValue(formValues);

        // Update our temporary tracking variable with full formatted address
        this.temporaryAddress = addressData.formattedAddress;

        // Store address components for later use when submitting the form
        // If unit_number is provided, use it instead of streetNumber
        const unitNumber = this.registrationForm.get('unit_number')?.value;
        this.addressComponents = {
            formatted_address: addressData.formattedAddress,
            location_lat: addressData.lat?.toString() || null,
            location_lng: addressData.lng?.toString() || null,
            street_number: unitNumber || addressData.streetNumber || null,
            street: addressData.street || null,
            city: addressData.city || null,
            state: addressData.state || null,
            country: addressData.country || null,
            country_code: addressData.countryCode || null,
            place_id: addressData.placeId || null,
        };

        // Update postal code validation status
        this.postalCodeValid = !!addressData.postalCode;
    }

    public onAddressValidationChanged(
        validation: AddressValidationResult
    ): void {
        const addressControl = this.registrationForm.get('residential_address');

        if (!validation.isValid && validation.errorMessage) {
            addressControl?.setErrors({
                addressError: validation.errorMessage,
            });
            addressControl?.markAsTouched();
        } else {
            // Clear address validation error if validation passes
            const currentErrors = addressControl?.errors;
            if (currentErrors) {
                delete currentErrors['addressError'];
                const hasOtherErrors = Object.keys(currentErrors).length > 0;
                addressControl?.setErrors(
                    hasOtherErrors ? currentErrors : null
                );
            }
        }

        this.registrationForm.updateValueAndValidity();
    }

    public toggleAddressMode(): void {
        // Simply toggle the mode - the new address component handles everything automatically
        this.addressMode = this.addressMode === 'single' ? 'split' : 'single';
    }
}
